<template>
  <teleport to="body">
    <!-- Overlay -->
    <transition
      name="modal-overlay"
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isVisible && dialogs.length > 0"
        class="fixed inset-0 bg-navy/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        @click="handleOverlayClick"
      >
        <!-- Modal Content -->
        <transition
          name="modal-content"
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <dialog
            v-if="currentDialog"
            ref="dialogRef"
            class="bg-white rounded-mh-xl shadow-lift max-w-md w-full mx-4 border-0 p-0"
            @click.stop
            :aria-labelledby="`confirm-title-${currentDialog.id}`"
            :aria-describedby="`confirm-message-${currentDialog.id}`"
            role="alertdialog"
            aria-modal="true"
            open
          >
            <!-- Header -->
            <div class="flex items-center p-6 pb-4">
              <!-- Icon -->
              <div :class="iconContainerClasses" class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4">
                <!-- Delete Icon -->
                <svg v-if="currentDialog.icon === 'delete'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <!-- Warning Icon -->
                <svg v-else-if="currentDialog.icon === 'warning'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <!-- Info Icon -->
                <svg v-else-if="currentDialog.icon === 'info'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Question Icon (default) -->
                <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <!-- Title -->
              <div class="flex-1">
                <h3 :id="`confirm-title-${currentDialog.id}`" :class="titleClasses" class="text-lg font-semibold leading-6">
                  {{ currentDialog.title }}
                </h3>
              </div>
            </div>

            <!-- Message -->
            <div class="px-6 pb-6">
              <p :id="`confirm-message-${currentDialog.id}`" class="text-sm leading-5 whitespace-pre-line" style="color: var(--text-sec)">
                {{ currentDialog.message }}
              </p>
            </div>

            <!-- Actions -->
            <div class="bg-light-gray px-6 py-4 rounded-b-mh-xl flex flex-col-reverse sm:flex-row gap-3">
              <!-- Cancel Button -->
              <button
                ref="cancelButton"
                @click="handleCancel"
                class="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-navy bg-white border border-grayish rounded-mh-md hover:bg-light-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy transition-colors"
              >
                {{ currentDialog.cancelText }}
              </button>

              <!-- Confirm Button -->
              <button
                ref="confirmButton"
                @click="handleConfirm"
                :class="confirmButtonClasses"
                class="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-white rounded-mh-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
              >
                {{ currentDialog.confirmText }}
              </button>
            </div>
          </dialog>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useConfirm } from '@/composables/useConfirm'
import { useModalAccessibility } from '@/composables/useAccessibility'

const { isVisible, dialogs, closeDialog } = useConfirm()
const { openModal, closeModal } = useModalAccessibility()

// Referencias a elementos
const dialogRef = ref<HTMLDialogElement>()
const cancelButton = ref<HTMLButtonElement>()
const confirmButton = ref<HTMLButtonElement>()

// Manejar accesibilidad del modal. Sin la condición sobre dialogRef aquí
// fuera: en la primera apertura el <dialog> aún no existe (watch pre-flush)
// y el trap de foco no llegaba a engancharse nunca; nextTick ya lo re-comprueba.
watch([isVisible, () => dialogs.length], ([visible, dialogCount]) => {
  if (visible && dialogCount > 0) {
    nextTick(() => {
      if (dialogRef.value && currentDialog.value) {
        openModal(dialogRef.value, {
          title: currentDialog.value.title,
          closeCallback: handleCancel
        })
      }
    })
  } else if (!visible) {
    closeModal({ title: 'Diálogo de confirmación' })
  }
})

// ========== COMPUTED ==========

/**
 * Diálogo actual (siempre el último)
 */
const currentDialog = computed(() => {
  return dialogs.length > 0 ? dialogs[dialogs.length - 1] : null
})

/**
 * Clases del contenedor del icono según el tipo
 */
const iconContainerClasses = computed(() => {
  if (!currentDialog.value) return ''

  const classes = {
    danger: 'bg-error/15 text-error',
    warning: 'bg-sage-soft text-sage',
    info: 'bg-navy-soft text-navy',
    success: 'bg-mint-soft text-sage'
  }

  return classes[currentDialog.value.type || 'info']
})

/**
 * Clases del título según el tipo
 */
const titleClasses = computed(() => {
  if (!currentDialog.value) return 'text-navy'

  const classes = {
    danger: 'text-error',
    warning: 'text-navy',
    info: 'text-navy',
    success: 'text-navy'
  }

  return classes[currentDialog.value.type || 'info']
})

/**
 * Clases del botón de confirmación según el tipo
 */
const confirmButtonClasses = computed(() => {
  if (!currentDialog.value) return 'bg-navy hover:bg-navy/85 focus:ring-navy'

  // sage-text como fondo: el sage claro dejaba el texto blanco a 2.2:1
  const classes = {
    danger: 'bg-error hover:opacity-90 focus:ring-error',
    warning: 'bg-sage-text hover:opacity-90 focus:ring-sage-text',
    info: 'bg-navy hover:bg-navy/85 focus:ring-navy',
    success: 'bg-sage-text hover:opacity-90 focus:ring-sage-text'
  }

  return classes[currentDialog.value.type || 'info']
})

// ========== MÉTODOS ==========

/**
 * Maneja el clic en el overlay (cerrar si no es persistente)
 */
const handleOverlayClick = () => {
  if (currentDialog.value && !currentDialog.value.persistent) {
    handleCancel()
  }
}

/**
 * Maneja la cancelación
 */
const handleCancel = () => {
  if (currentDialog.value) {
    closeDialog(currentDialog.value.id, false)
  }
}

/**
 * Maneja la confirmación
 */
const handleConfirm = () => {
  if (currentDialog.value) {
    closeDialog(currentDialog.value.id, true)
  }
}

/**
 * Maneja teclas especiales.
 *
 * Solo Escape se gestiona globalmente: Enter lo resuelven los botones de
 * forma nativa según dónde esté el foco (que el trap de foco coloca en
 * "Cancelar" al abrir). Un Enter global confirmaba acciones destructivas
 * aunque el usuario estuviera sobre "Cancelar".
 */
const handleKeydown = (event: KeyboardEvent) => {
  if (!currentDialog.value) return

  if (event.key === 'Escape' && !currentDialog.value.persistent) {
    handleCancel()
  }
}

// ========== LIFECYCLE ==========

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Transiciones para el modal */
.modal-overlay-enter-active,
.modal-overlay-leave-active,
.modal-content-enter-active,
.modal-content-leave-active {
  transition: all 0.2s ease;
}
</style>